<template>
	<li class="bg-white border rounded p-4 flex flex-col justify-between gap-3">
		<div class="flex items-center gap-2">
			<img
				v-if="icon"
				:src="icon"
				:alt="`${name} logo`"
				class="shrink-0 h-8 w-8 object-contain"
			/>
			<div class="flex flex-col">
				<span class="font-semibold text-sm">
					{{ name }}
				</span>
			</div>
		</div>
		<div
			v-if="connection || connectionNumberEditable"
			class="flex flex-col self-center justify-center relative w-full"
		>
			<div class="flex flex-col">
				<span class="text-sm text-gray-500">
					{{
						showConnectionNumber ? "Directe aanstelling:" : "Aangesloten via:"
					}}
				</span>
				<div class="flex gap-1 h-8 justify-stretch">
					<div
						v-if="showConnectionNumber"
						class="flex flex-col justify-stretch relative group flex-auto"
						@click="connectionNumberEditable = true"
					>
						<AppButton
							v-if="!connectionNumberEditable && connection?.connectionNumber"
							variant="ghost"
							icon-right="edit"
							class="px-2 py-1 grow border rounded bg-gray-100 text-gray-600 text-sm text-start justify-between font-normal [&_.ht-icon]:text-[14px] [&_.ht-icon]:text-gray-400 [&:hover_.ht-icon]:text-gray-600"
							@click="connectionNumberEditable = true"
						>
							{{ connection?.connectionNumber }}
						</AppButton>
						<input
							v-else
							ref="input"
							type="text"
							class="px-2 py-1 border rounded text-sm flex-auto w-full"
							:value="connection?.connectionNumber"
							@blur="onBlur"
							@keydown.escape="onEscape"
							@keydown.enter="onEnter"
						/>
					</div>
					<select
						v-model="connectionType"
						class="border rounded hover:bg-gray-100 text-sm"
						:class="
							showConnectionNumber
								? 'appearance-none text-[0px] size-8 bg-(image:--arrow-down-gray) bg-no-repeat bg-center shrink-0'
								: 'grow'
						"
					>
						<option value="direct">Directe aanstelling</option>
						<option
							v-for="sp in lender.provider.supportedServiceProviders"
							:key="sp.id"
							:value="sp.id"
						>
							{{ sp.name }}
						</option>
					</select>
				</div>
			</div>
		</div>
		<div v-else class="flex flex-col relative group">
			<AppButton
				variant="primary"
				class="self-start text-sm"
				icon="dashboard_customize"
				@click="connectionNumberEditable = true"
			>
				Koppelen
			</AppButton>
		</div>
	</li>
</template>

<script lang="ts" setup>
import type { LenderRecord } from "@/composables/useLender";
import type { Schema } from "@loanlink-nl/zero";
import type { Row } from "@rocicorp/zero";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

type ProviderWithConnections = Row<Schema["tables"]["hdnProvider"]> & {
	supportedServiceProviders: Readonly<
		Row<Schema["tables"]["hdnServiceProvider"]>[]
	>;
	connections: Readonly<
		(Row<Schema["tables"]["hdnLenderConnection"]> & {
			serviceProvider?: Row<Schema["tables"]["hdnServiceProvider"]>;
		})[]
	>;
};

const props = defineProps<{
	lender: LenderRecord & {
		provider: ProviderWithConnections;
		displayName: string;
	};
}>();

const icon = computed(() => {
	if (!props.lender.icon) {
		return undefined;
	}

	return `/logos/${props.lender.icon}`;
});

const name = computed(() => {
	return props.lender.displayName;
});

const emit = defineEmits<{
	(e: "update:connectionNumber", connectionNumber?: string): void;
	(e: "update:serviceProvider", serviceProviderId: string): void;
}>();

const connectionType = ref();
const inputEl = useTemplateRef("input");

const connection = computed(() => {
	const [connection] = props.lender.provider.connections;
	return connection;
});

const showConnectionNumber = computed(() => {
	if (connection.value?.sendMethod === "service_provider") {
		return false;
	}

	if (!connection.value?.sendMethod && connection.value?.serviceProviderId) {
		return false;
	}

	return true;
});

watch(
	connection,
	() => {
		if (!connection.value) {
			return;
		}

		if (connection.value.connectionNumber) {
			if (
				!connection.value.sendMethod ||
				connection.value.sendMethod === "direct"
			) {
				connectionType.value = "direct";
			}
		}

		if (connection.value.serviceProviderId) {
			if (
				connection.value.sendMethod === "service_provider" ||
				(!connection.value.sendMethod && !connection.value.connectionNumber)
			) {
				connectionType.value = connection.value.serviceProviderId;
			}
		}
	},
	{ immediate: true },
);

watch(connectionType, (connectionType, prev) => {
	if (connectionType === "direct" && prev && prev !== "direct") {
		emit(
			"update:connectionNumber",
			connection.value?.connectionNumber ?? undefined,
		);
		connectionNumberEditable.value = true;
	} else if (connectionType !== "direct") {
		emit("update:serviceProvider", connectionType);
	}
});

let escaped = false;
let entered = false;

function onBlur() {
	if (escaped || entered) {
		escaped = false;
		entered = false;
		return;
	}

	connectionNumberEditable.value = false;
	updateConnectionNumber(inputEl.value?.value);
}

function onEscape() {
	escaped = true;
	connectionNumberEditable.value = false;
}

function onEnter() {
	entered = true;

	connectionNumberEditable.value = false;
	updateConnectionNumber(inputEl.value?.value);
}

function updateConnectionNumber(newNumber?: string) {
	emit("update:connectionNumber", newNumber);
}

const connectionNumberEditable = ref(false);
watch(connectionNumberEditable, async () => {
	if (connectionNumberEditable.value) {
		await nextTick(() => {
			inputEl.value?.select();
		});
	}
});
</script>
